import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Menu,
  MenuItem,
  Checkbox,
  Grid,
  CircularProgress,
  Chip,
  Autocomplete
} from '@mui/material';
import { FactCheck as AuditIcon, ViewColumn as ViewColumnIcon } from '@mui/icons-material';
import api from '../../utils/api';

const MODULES = ['', 'contacts', 'accounts', 'jobs', 'documents', 'employees', 'timesheets', 'training', 'users', 'roles', 'groups', 'auth'];
const ACTIONS = ['', 'create', 'update', 'delete', 'view', 'upload', 'download', 'login', 'logout'];

const formatUser = (u) => {
  if (!u) return '—';
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name || u.email || '—';
};

const AdminAuditTrail = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [entityType, setEntityType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [colMenuAnchorEl, setColMenuAnchorEl] = useState(null);
  const [visibleCols, setVisibleCols] = useState({
    when: true,
    user: true,
    module: true,
    action: true,
    entity: true,
    description: true
  });

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/users?limit=300');
      setUsers(res.data?.data || []);
    } catch {
      setUsers([]);
    }
  }, []);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage)
      });
      if (moduleFilter) params.append('module', moduleFilter);
      if (actionFilter) params.append('action', actionFilter);
      if (selectedUser?.id) params.append('user_id', selectedUser.id);
      if (entityType.trim()) params.append('entity_type', entityType.trim());
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (debouncedSearch.trim()) params.append('search', debouncedSearch.trim());

      const res = await api.get(`/activities?${params.toString()}`);
      setRows(res.data?.data || []);
      setTotal(res.data?.pagination?.total ?? 0);
    } catch (e) {
      console.error(e);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, moduleFilter, actionFilter, selectedUser, entityType, startDate, endDate, debouncedSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);
  const visibleColCount =
    (visibleCols.when ? 1 : 0) +
    (visibleCols.user ? 1 : 0) +
    (visibleCols.module ? 1 : 0) +
    (visibleCols.action ? 1 : 0) +
    (visibleCols.entity ? 1 : 0) +
    (visibleCols.description ? 1 : 0);

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AuditIcon sx={{ fontSize: 32 }} />
        Audit trail
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        System-wide activity log: who created, updated, deleted, uploaded, and more. Data comes from the same log used for compliance and troubleshooting.
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Search description / entity"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Module"
                value={moduleFilter}
                onChange={(e) => {
                  setModuleFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {MODULES.filter(Boolean).map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Action"
                value={actionFilter}
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {ACTIONS.filter(Boolean).map((a) => (
                  <MenuItem key={a} value={a}>
                    {a}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                size="small"
                options={users}
                getOptionLabel={(o) => formatUser(o) + (o.email ? ` (${o.email})` : '')}
                value={selectedUser}
                onChange={(_, v) => {
                  setSelectedUser(v);
                  setPage(0);
                }}
                renderInput={(params) => <TextField {...params} label="User" />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Entity type"
                placeholder="e.g. contact, job"
                value={entityType}
                onChange={(e) => {
                  setEntityType(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ViewColumnIcon fontSize="small" />}
                onClick={(e) => setColMenuAnchorEl(e.currentTarget)}
                type="button"
              >
                Columns
              </Button>
              <Menu
                anchorEl={colMenuAnchorEl}
                open={Boolean(colMenuAnchorEl)}
                onClose={() => setColMenuAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {[
                  ['when', 'When'],
                  ['user', 'User'],
                  ['module', 'Module'],
                  ['action', 'Action'],
                  ['entity', 'Entity'],
                  ['description', 'Description']
                ].map(([key, label]) => (
                  <MenuItem
                    key={key}
                    onClick={() => setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }))}
                    dense
                  >
                    <Checkbox size="small" checked={Boolean(visibleCols[key])} />
                    <Typography variant="body2">{label}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {visibleCols.when && <TableCell sx={{ fontWeight: 600 }}>When</TableCell>}
                    {visibleCols.user && <TableCell sx={{ fontWeight: 600 }}>User</TableCell>}
                    {visibleCols.module && <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>}
                    {visibleCols.action && <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>}
                    {visibleCols.entity && <TableCell sx={{ fontWeight: 600 }}>Entity</TableCell>}
                    {visibleCols.description && <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColCount} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No activities match your filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id} hover>
                        {visibleCols.when && <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {row.performed_at ? new Date(row.performed_at).toLocaleString() : '—'}
                        </TableCell>}
                        {visibleCols.user && <TableCell>{formatUser(row.user)}</TableCell>}
                        {visibleCols.module && <TableCell>
                          <Chip label={row.module || '—'} size="small" variant="outlined" />
                        </TableCell>}
                        {visibleCols.action && <TableCell>
                          <Chip label={row.action || '—'} size="small" color="primary" variant="outlined" />
                        </TableCell>}
                        {visibleCols.entity && <TableCell>
                          <Typography variant="body2" component="span">
                            {row.entity_type || '—'}
                          </Typography>
                          {row.entity_name && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {row.entity_name}
                            </Typography>
                          )}
                        </TableCell>}
                        {visibleCols.description && <TableCell sx={{ maxWidth: 360 }}>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {row.description}
                          </Typography>
                        </TableCell>}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Card>
    </Box>
  );
};

export default AdminAuditTrail;
